/**
 * Wave chat-instant · ChatWindow
 *
 * Janela de chat estilo WhatsApp/iMessage. SEPARADA visualmente do
 * formato email/inbox: bubbles alinhadas por autor, sem subject,
 * emojis inline, reactions chip embaixo, typing indicator no header.
 *
 * Desktop: floating bottom-right 360×560, side-by-side se houver
 *          múltiplas janelas (offset via prop `slot`).
 * Mobile:  full-screen overlay z-50, fecha com back ou swipe-down do header.
 *
 * Polling: 3s (rápido) enquanto a window está aberta. Para quando fecha.
 * Presence ping: a cada 25s + a cada blur/keystroke (typing).
 *
 * Bubble visual contract:
 *   - Própria: alinhada direita, bg-brand-500/15, text-ink, rounded-2xl
 *              + rounded-tr-md (tail subtle no canto top-right)
 *   - Peer:    alinhada esquerda, bg-surface-3, text-ink, rounded-2xl
 *              + rounded-tl-md
 *   - Entrada: animate-fade-in-up 150ms ease-out (Emil bubble-enter)
 *   - Timestamp grouped: cada 5min mostra cabeçalho de hora
 *   - Reactions: chip discreto abaixo da bubble (👍 2 · ❤️ 1)
 *   - Reaction picker: 6 emojis fixed (👍 ❤️ 😂 🎉 👀 🙏)
 *
 * Sound: tocar pop sutil quando chega msg de peer (toggle setting).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { useAuth } from '@/store/auth.store';
import { useChatInstantStore } from '@/store/chat-instant.store';
import { chatInstantApi } from '@/api/chat-instant';
import type { ChatInstantMessage } from '@/api/chat-instant';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';
// Wave chat-identity-fix · 2026-05-26 — usa JWT.sub como autoridade pra
// "é meu?". Veja apps/web/src/lib/auth-identity.ts pra rationale.
import { useAuthoritativeUserId } from '@/lib/auth-identity';
import { parseServerDate } from '@/lib/parse-server-date';

const MSG_POLL_MS = 3_000;
const PRESENCE_PING_MS = 25_000;
const TYPING_DEBOUNCE_MS = 1_500;

// Set fixo de emojis no picker — manter pequeno (Emil: opções não ajudam,
// defaults bons sim). 6 emojis cobrem ~95% das reactions reais em chats.
const QUICK_EMOJIS = ['👍', '❤️', '😂', '🎉', '👀', '🙏'] as const;

// Emojis frequentes do composer (separados dos reactions — escolha
// editorial: emojis no body são mais variados).
const COMPOSER_EMOJIS = [
  '👍', '❤️', '😂', '🎉', '👀', '🙏',
  '👏', '🔥', '😊', '🤔', '😅', '😢',
  '🙌', '💪', '✅', '❌', '⚠️', '🚧',
  '🏗️', '📐', '🔨', '🧱', '📷', '📎',
  '🚀', '💯', '🤝', '👋', '☕', '🍕',
];

export interface ChatWindowProps {
  conversationId: string;
  /** Posição horizontal no array de janelas abertas (desktop only). */
  slot: number;
  /** Soundplay toggle vindo do setting global. */
  soundEnabled?: boolean;
}

export function ChatWindow({ conversationId, slot, soundEnabled = true }: ChatWindowProps) {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const me = useAuth((s) => s.user);
  // Wave chat-identity-fix · prioriza JWT.sub sobre me.id persistido.
  // Bubble vermelha/direita só renderiza certo se `meId` casar com
  // `author_user_id` que o backend gravou — e o backend usa JWT.sub.
  const meId = useAuthoritativeUserId();

  const conv = useChatInstantStore((s) =>
    s.conversations.find((c) => c.id === conversationId),
  );
  const win = useChatInstantStore((s) => s.windows.get(conversationId));
  const messages = useChatInstantStore(
    (s) => s.messagesByConv.get(conversationId) ?? [],
  );
  const presenceMap = useChatInstantStore((s) => s.presence);

  const closeWindow = useChatInstantStore((s) => s.closeWindow);
  const minimizeWindow = useChatInstantStore((s) => s.minimizeWindow);
  const setMessages = useChatInstantStore((s) => s.setMessages);
  const appendMessage = useChatInstantStore((s) => s.appendMessage);
  const removeMessage = useChatInstantStore((s) => s.removeMessage);
  const patchReactions = useChatInstantStore((s) => s.patchReactions);
  const decrementUnread = useChatInstantStore((s) => s.decrementUnread);
  const setPresence = useChatInstantStore((s) => s.setPresence);

  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [reactingTo, setReactingTo] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const typingTimerRef = useRef<number | null>(null);
  const lastTypingPingRef = useRef<number>(0);
  const prevMsgCountRef = useRef<number>(0);

  // ── Polling de mensagens ───────────────────────────────────────────
  const refresh = useCallback(async () => {
    try {
      const msgs = await chatInstantApi.getMessages(conversationId, { limit: 80 });
      setMessages(conversationId, msgs);
    } catch {
      // silencioso
    }
  }, [conversationId, setMessages]);

  useEffect(() => {
    if (!win?.open) return;
    void refresh();
    const id = window.setInterval(refresh, MSG_POLL_MS);
    return () => window.clearInterval(id);
  }, [win?.open, refresh]);

  // ── Marca read ao abrir / quando chega msg nova ────────────────────
  useEffect(() => {
    if (!win?.open) return;
    if (!conv || conv.unreadCount === 0) return;
    void chatInstantApi
      .markRead(conversationId)
      .then(() => decrementUnread(conversationId))
      .catch(() => {});
  }, [win?.open, conv, conversationId, decrementUnread]);

  // ── Auto-scroll bottom on new msg ──────────────────────────────────
  useEffect(() => {
    if (messages.length > prevMsgCountRef.current) {
      const el = scrollRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
      // Som notification se peer message new (não a minha).
      const last = messages[messages.length - 1];
      if (
        soundEnabled &&
        prevMsgCountRef.current > 0 &&
        last &&
        last.authorUserId !== meId
      ) {
        playPop();
      }
    }
    prevMsgCountRef.current = messages.length;
  }, [messages.length, soundEnabled, messages, meId]);

  // ── Presence ping (heartbeat + typing) ─────────────────────────────
  useEffect(() => {
    if (!win?.open) return;
    let alive = true;
    const tick = async () => {
      try {
        await chatInstantApi.ping(null);
      } catch {}
      if (!alive) return;
      // Atualiza presence dos peers da conv (DMs).
      if (conv && conv.type === 'dm') {
        const peer = conv.members.find((m) => m.userId !== meId);
        if (peer) {
          try {
            const p = await chatInstantApi.getPresence([peer.userId]);
            if (alive) setPresence(p);
          } catch {}
        }
      }
    };
    void tick();
    const id = window.setInterval(tick, PRESENCE_PING_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [win?.open, conv, meId, setPresence]);

  // ── Typing throttle: ping com typingIn=conv enquanto digita ────────
  const notifyTyping = useCallback(() => {
    const now = Date.now();
    // Throttle: 1 ping a cada 2s no máximo.
    if (now - lastTypingPingRef.current > 2_000) {
      lastTypingPingRef.current = now;
      void chatInstantApi.ping(conversationId).catch(() => {});
    }
    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
    typingTimerRef.current = window.setTimeout(() => {
      void chatInstantApi.ping(null).catch(() => {});
    }, TYPING_DEBOUNCE_MS);
  }, [conversationId]);

  // ── Send ──────────────────────────────────────────────────────────
  const send = useCallback(async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    // Otimismo: insere localmente com id temp.
    const tempId = `temp-${Date.now()}`;
    const tempMsg: ChatInstantMessage = {
      id: tempId,
      conversationId,
      conversationType: conv?.type ?? 'dm',
      // Wave chat-identity-fix · usa o id autoritativo (JWT.sub). Se o
      // backend gravar a msg com `author_user_id = JWT.sub` e a UI usar
      // o mesmo aqui, o bubble não troca de lado quando o servidor
      // ecoar a msg de volta.
      authorUserId: meId ?? '',
      body: text,
      replyToId: null,
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 86_400_000).toISOString(),
      deletedAt: null,
      reactions: [],
    };
    appendMessage(conversationId, tempMsg);
    setBody('');
    try {
      const real = await chatInstantApi.sendMessage(conversationId, text);
      removeMessage(conversationId, tempId);
      appendMessage(conversationId, real);
    } catch {
      removeMessage(conversationId, tempId);
      setBody(text); // restore para o user retentar
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [body, sending, conversationId, conv?.type, meId, appendMessage, removeMessage]);

  // ── Reactions ─────────────────────────────────────────────────────
  const toggleReaction = useCallback(
    async (messageId: string, emoji: string) => {
      // Wave chat-identity-fix · usa o id autoritativo capturado no topo.
      const reactingMeId = meId ?? '';
      const existing = messages.find((m) => m.id === messageId);
      if (!existing) return;
      const group = existing.reactions.find((r) => r.emoji === emoji);
      const hasReacted = group?.users.includes(reactingMeId) ?? false;
      // Otimismo local
      patchReactions(conversationId, messageId, (reactions) => {
        const idx = reactions.findIndex((r) => r.emoji === emoji);
        if (hasReacted) {
          if (idx === -1) return reactions;
          const target = reactions[idx];
          if (!target) return reactions;
          const newUsers = target.users.filter((u) => u !== reactingMeId);
          if (newUsers.length === 0) {
            return reactions.filter((_, i) => i !== idx);
          }
          const next = [...reactions];
          next[idx] = { emoji: target.emoji, users: newUsers };
          return next;
        }
        if (idx === -1) {
          return [...reactions, { emoji, users: [reactingMeId] }];
        }
        const target = reactions[idx];
        if (!target) return reactions;
        const next = [...reactions];
        next[idx] = { emoji: target.emoji, users: [...target.users, reactingMeId] };
        return next;
      });
      try {
        if (hasReacted) {
          await chatInstantApi.removeReaction(messageId, emoji);
        } else {
          await chatInstantApi.addReaction(messageId, emoji);
        }
      } catch {
        // Reverte com refresh.
        void refresh();
      }
      setReactingTo(null);
    },
    [meId, messages, conversationId, patchReactions, refresh],
  );

  // ── Keyboard handler do composer ──────────────────────────────────
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    } else {
      notifyTyping();
    }
  };

  // ── Computed: peer name + presence ─────────────────────────────────
  const peerName = useMemo(() => {
    if (!conv) return '';
    if (conv.type === 'group') return conv.name ?? (isEn ? 'Group' : 'Grupo');
    const p = conv.members.find((m) => m.userId !== meId);
    return p?.fullName ?? (isEn ? '(you)' : '(você)');
  }, [conv, meId, isEn]);

  const peerId = useMemo(() => {
    if (!conv || conv.type !== 'dm') return null;
    return conv.members.find((m) => m.userId !== meId)?.userId ?? null;
  }, [conv, meId]);

  const peerPresence = peerId ? presenceMap.get(peerId) : undefined;
  const peerOnline = peerPresence?.online ?? conv?.peerOnline ?? false;
  const peerTyping = peerPresence?.isTypingIn === conversationId;

  const subtitle = peerTyping
    ? t.chatInstant.window.typing(peerName)
    : peerOnline
      ? t.chatInstant.window.online
      : peerPresence?.lastSeenAt
        ? t.chatInstant.window.offline(relativeShort(peerPresence.lastSeenAt, isEn))
        : '';

  // ── Render guards ──────────────────────────────────────────────────
  // Wave chat-identity-fix · meId vem de useAuthoritativeUserId (JWT.sub).
  // Se ele for null, nem renderiza — comparar bubbles sem identidade
  // confiável é o que causou o bug de "msg apareceu como se peer enviasse".
  if (!me || !meId || !win?.open || !conv) return null;

  // Slot offset desktop. Cada window 360px de largura + 12px gap. Bottom: 0
  // (encostado no chão), right escala com slot.
  const desktopRight = 12 + slot * 376;

  const containerStyle: CSSProperties = {
    right: `${desktopRight}px`,
  };

  return (
    <section
      role="dialog"
      aria-label={`Chat com ${peerName}`}
      data-testid="chat-instant-window"
      style={containerStyle}
      className={cn(
        'fixed z-50 flex flex-col bg-surface-2 border-line shadow-pop dark:shadow-pop-dark',
        // Desktop: floating bottom-right card
        'hidden md:flex md:bottom-0 md:h-[560px] md:w-[360px] md:rounded-t-2xl md:border md:border-b-0',
        'md:animate-fade-in-up',
      )}
    >
      <WindowHeader
        peerName={peerName}
        subtitle={subtitle}
        online={peerOnline}
        onMinimize={() => minimizeWindow(conversationId)}
        onClose={() => closeWindow(conversationId)}
      />
      <BodyAndComposer
        messages={messages}
        meId={meId}
        peerName={peerName}
        sending={sending}
        body={body}
        onBodyChange={setBody}
        onKeyDown={onKeyDown}
        onSend={send}
        onToggleEmoji={() => setEmojiOpen((v) => !v)}
        emojiOpen={emojiOpen}
        onInsertEmoji={(e) => {
          setBody((b) => b + e);
          inputRef.current?.focus();
        }}
        reactingTo={reactingTo}
        onStartReacting={setReactingTo}
        onToggleReaction={toggleReaction}
        scrollRef={scrollRef}
        inputRef={inputRef}
      />

      {/* Mobile full-screen overlay — render separado para escapar do md:flex */}
      <MobileMirror
        conversationId={conversationId}
        peerName={peerName}
        subtitle={subtitle}
        online={peerOnline}
        messages={messages}
        meId={meId}
        sending={sending}
        body={body}
        onBodyChange={setBody}
        onKeyDown={onKeyDown}
        onSend={send}
        emojiOpen={emojiOpen}
        onToggleEmoji={() => setEmojiOpen((v) => !v)}
        onInsertEmoji={(e) => {
          setBody((b) => b + e);
          inputRef.current?.focus();
        }}
        reactingTo={reactingTo}
        onStartReacting={setReactingTo}
        onToggleReaction={toggleReaction}
        onClose={() => closeWindow(conversationId)}
      />
    </section>
  );
}

// ── Window header (desktop) ──────────────────────────────────────────

function WindowHeader({
  peerName,
  subtitle,
  online,
  onMinimize,
  onClose,
}: {
  peerName: string;
  subtitle: string;
  online: boolean;
  onMinimize: () => void;
  onClose: () => void;
}) {
  const { t } = useT();
  const initials = peerName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <header className="flex items-center gap-3 border-b border-line px-3 py-2.5 bg-surface-2 rounded-t-2xl">
      <div className="relative shrink-0">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-500/15 text-[11px] font-semibold text-brand-600">
          {initials || '?'}
        </div>
        <span
          aria-label={online ? 'Online' : 'Offline'}
          className={cn(
            'absolute bottom-0 right-0 h-2 w-2 rounded-full ring-2 ring-surface-2',
            online ? 'bg-ok' : 'bg-ink-soft',
          )}
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink leading-tight">{peerName}</p>
        {subtitle && (
          <p
            data-testid="chat-instant-subtitle"
            className="truncate text-[11px] text-ink-subtle leading-tight"
          >
            {subtitle}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onMinimize}
        aria-label={t.chat.minimize}
        className="pressable h-7 w-7 rounded-full text-ink-muted hover:bg-surface-3 hover:text-ink transition-colors duration-120 flex items-center justify-center"
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3 13h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
      <button
        type="button"
        onClick={onClose}
        aria-label={t.common.close}
        className="pressable h-7 w-7 rounded-full text-ink-muted hover:bg-surface-3 hover:text-ink transition-colors duration-120 flex items-center justify-center"
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
    </header>
  );
}

// ── Shared body+composer block ───────────────────────────────────────

interface BodyAndComposerProps {
  messages: ChatInstantMessage[];
  meId: string;
  peerName: string;
  sending: boolean;
  body: string;
  onBodyChange: (s: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  emojiOpen: boolean;
  onToggleEmoji: () => void;
  onInsertEmoji: (e: string) => void;
  reactingTo: string | null;
  onStartReacting: (messageId: string | null) => void;
  onToggleReaction: (messageId: string, emoji: string) => void;
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
  inputRef: React.MutableRefObject<HTMLTextAreaElement | null>;
}

function BodyAndComposer(props: BodyAndComposerProps) {
  const {
    messages,
    meId,
    sending,
    body,
    onBodyChange,
    onKeyDown,
    onSend,
    emojiOpen,
    onToggleEmoji,
    onInsertEmoji,
    reactingTo,
    onStartReacting,
    onToggleReaction,
    scrollRef,
    inputRef,
  } = props;
  const { t, locale } = useT();
  const isEn = locale === 'en';

  return (
    <>
      {/* Body */}
      <div
        ref={scrollRef}
        data-testid="chat-instant-scroll"
        className="flex-1 overflow-y-auto px-3 py-3 space-y-1 bg-surface"
      >
        {messages.length === 0 && (
          <p className="text-center text-[11px] text-ink-subtle py-8">
            {t.chatInstant.window.empty}
          </p>
        )}
        {renderBubbles(messages, meId, isEn, reactingTo, onStartReacting, onToggleReaction)}
      </div>

      {/* Composer */}
      <div className="relative border-t border-line bg-surface-2 px-2 py-2 rounded-b-none">
        {emojiOpen && (
          <div
            data-testid="chat-instant-composer-emojis"
            className={cn(
              'absolute bottom-full left-2 right-2 mb-2 grid grid-cols-10 gap-1',
              'rounded-xl border border-line bg-surface-2 p-2 shadow-pop dark:shadow-pop-dark',
              'animate-fade-in-up',
            )}
          >
            {COMPOSER_EMOJIS.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => onInsertEmoji(e)}
                className="pressable h-7 w-7 rounded-md text-base hover:bg-surface-3 transition-colors duration-120"
                aria-label={`Inserir ${e}`}
              >
                {e}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-1.5">
          <button
            type="button"
            onClick={onToggleEmoji}
            aria-label={t.chatInstant.window.openEmojis}
            className={cn(
              'pressable shrink-0 flex h-11 w-11 items-center justify-center rounded-full',
              'text-ink-muted hover:bg-surface-3 hover:text-ink transition-colors duration-120',
              emojiOpen && 'bg-surface-3 text-ink',
            )}
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.4" />
              <circle cx="7.5" cy="8.5" r="0.8" fill="currentColor" />
              <circle cx="12.5" cy="8.5" r="0.8" fill="currentColor" />
              <path d="M6.5 12c.8 1.2 2 1.8 3.5 1.8s2.7-.6 3.5-1.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none" />
            </svg>
          </button>
          <AutoExpandingTextarea
            ref={inputRef}
            value={body}
            onChange={onBodyChange}
            onKeyDown={onKeyDown}
            placeholder={t.chatInstant.window.placeholder}
            disabled={sending}
          />
          <button
            type="button"
            onClick={onSend}
            disabled={!body.trim() || sending}
            aria-label={t.chatInstant.window.send}
            className={cn(
              'pressable shrink-0 flex h-11 w-11 items-center justify-center rounded-full',
              'transition-colors duration-120',
              body.trim() && !sending
                ? 'bg-brand-500 text-white hover:bg-brand-600'
                : 'bg-surface-3 text-ink-soft cursor-not-allowed',
            )}
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M2 8l12-6-4 14-3-6-5-2Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" fill="none" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}

// ── Bubble renderer ──────────────────────────────────────────────────

function renderBubbles(
  messages: ChatInstantMessage[],
  meId: string,
  isEn: boolean,
  reactingTo: string | null,
  onStartReacting: (messageId: string | null) => void,
  onToggleReaction: (messageId: string, emoji: string) => void,
) {
  const nodes: React.ReactNode[] = [];
  let lastTimestamp: number | null = null;

  messages.forEach((m, idx) => {
    const isMine = m.authorUserId === meId;
    const ts = new Date(m.createdAt).getTime();
    // Grouping de timestamp a cada 5min.
    if (lastTimestamp === null || ts - lastTimestamp > 5 * 60_000) {
      nodes.push(
        <div
          key={`ts-${m.id}`}
          data-testid="chat-instant-timestamp"
          className="text-center text-[10px] text-ink-subtle py-2 select-none"
        >
          {formatGroupTimestamp(m.createdAt, isEn)}
        </div>,
      );
      lastTimestamp = ts;
    }

    nodes.push(
      <Bubble
        key={m.id}
        message={m}
        isMine={isMine}
        reactionPickerOpen={reactingTo === m.id}
        onOpenReactionPicker={() => onStartReacting(m.id)}
        onCloseReactionPicker={() => onStartReacting(null)}
        onToggleReaction={(emoji) => onToggleReaction(m.id, emoji)}
        meId={meId}
        // Stagger só nas 5 últimas (cosmetic, evita lag em listas longas)
        delayMs={idx >= messages.length - 5 ? (idx - (messages.length - 5)) * 30 : 0}
      />,
    );
  });

  return nodes;
}

// ── Bubble individual ────────────────────────────────────────────────

function Bubble({
  message,
  isMine,
  reactionPickerOpen,
  onOpenReactionPicker,
  onCloseReactionPicker,
  onToggleReaction,
  meId,
  delayMs,
}: {
  message: ChatInstantMessage;
  isMine: boolean;
  reactionPickerOpen: boolean;
  onOpenReactionPicker: () => void;
  onCloseReactionPicker: () => void;
  onToggleReaction: (emoji: string) => void;
  meId: string;
  delayMs: number;
}) {
  // Long-press mobile + hover desktop ambos abrem o picker.
  const pressTimerRef = useRef<number | null>(null);
  const onPressStart = () => {
    pressTimerRef.current = window.setTimeout(() => {
      onOpenReactionPicker();
    }, 420);
  };
  const onPressEnd = () => {
    if (pressTimerRef.current) {
      window.clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  return (
    <div
      data-testid={isMine ? 'chat-instant-bubble-mine' : 'chat-instant-bubble-peer'}
      className={cn(
        'group relative flex items-end gap-1.5 mb-1',
        isMine ? 'justify-end' : 'justify-start',
      )}
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <div className={cn('max-w-[80%]', 'animate-fade-in-up')}>
        <div
          onPointerDown={onPressStart}
          onPointerUp={onPressEnd}
          onPointerLeave={onPressEnd}
          className={cn(
            'relative px-3 py-2 text-sm leading-snug',
            'rounded-2xl break-words whitespace-pre-wrap',
            isMine
              ? 'bg-brand-500/15 text-ink rounded-tr-md'
              : 'bg-surface-3 text-ink rounded-tl-md',
          )}
        >
          {message.body}
        </div>

        {/* Reactions chip */}
        {message.reactions.length > 0 && (
          <div
            data-testid="chat-instant-reactions"
            className={cn(
              'mt-1 flex flex-wrap gap-1',
              isMine ? 'justify-end' : 'justify-start',
            )}
          >
            {message.reactions.map((r) => {
              const isMineReaction = r.users.includes(meId);
              return (
                <button
                  key={r.emoji}
                  type="button"
                  onClick={() => onToggleReaction(r.emoji)}
                  className={cn(
                    'pressable inline-flex items-center gap-1 rounded-full px-1.5 py-0.5',
                    'text-[11px] border transition-colors duration-120',
                    isMineReaction
                      ? 'border-brand-500/40 bg-brand-500/15 text-ink'
                      : 'border-line bg-surface-2 text-ink-muted hover:bg-surface-3',
                  )}
                  aria-label={`${r.emoji} (${r.users.length})`}
                >
                  <span aria-hidden>{r.emoji}</span>
                  <span className="tabular-nums">{r.users.length}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Reaction trigger (hover desktop) — small ghost button beside bubble */}
      <button
        type="button"
        onClick={onOpenReactionPicker}
        aria-label="Reagir"
        className={cn(
          'pressable shrink-0 h-7 w-7 rounded-full bg-surface-2 border border-line',
          'opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-150',
          'hidden md:flex items-center justify-center text-ink-muted hover:text-ink',
        )}
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" />
          <path d="M5 9.5c.5 1 1.6 1.5 3 1.5s2.5-.5 3-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none" />
        </svg>
      </button>

      {reactionPickerOpen && (
        <div
          data-testid="chat-instant-reaction-picker"
          className={cn(
            'absolute z-10 -top-9 flex gap-0.5 rounded-full border border-line bg-surface-2',
            'shadow-pop dark:shadow-pop-dark px-1.5 py-1 animate-fade-in-up',
            isMine ? 'right-0' : 'left-0',
          )}
          onMouseLeave={onCloseReactionPicker}
        >
          {QUICK_EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => onToggleReaction(e)}
              // Touch target ≥44pt mantém HIG; visual da picker fica
              // compacto com text-base + padding zero entre items.
              className="pressable h-11 w-11 rounded-full text-lg hover:bg-surface-3 transition-colors duration-120 flex items-center justify-center"
              aria-label={`Reagir com ${e}`}
            >
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Mobile mirror (full-screen) ──────────────────────────────────────

function MobileMirror(props: {
  conversationId: string;
  peerName: string;
  subtitle: string;
  online: boolean;
  messages: ChatInstantMessage[];
  meId: string;
  sending: boolean;
  body: string;
  onBodyChange: (s: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  emojiOpen: boolean;
  onToggleEmoji: () => void;
  onInsertEmoji: (e: string) => void;
  reactingTo: string | null;
  onStartReacting: (id: string | null) => void;
  onToggleReaction: (id: string, e: string) => void;
  onClose: () => void;
}) {
  const { t } = useT();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [props.messages.length]);

  const initials = props.peerName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div
      data-testid="chat-instant-window-mobile"
      className={cn(
        'fixed inset-0 z-50 flex-col bg-surface md:hidden',
        'animate-[mobileChatIn_240ms_cubic-bezier(0.23,1,0.32,1)_both]',
        'flex',
        'pt-[env(safe-area-inset-top)]',
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      <style>{`
        @keyframes mobileChatIn {
          0% { transform: translateX(8%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      <header className="flex items-center gap-3 border-b border-line px-3 py-2.5 bg-surface-2">
        <button
          type="button"
          onClick={props.onClose}
          aria-label={t.common.back}
          className="pressable flex h-11 w-11 items-center justify-center rounded-full text-ink-muted hover:bg-surface-3 hover:text-ink"
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none">
            <path d="M12 4l-6 6 6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="relative">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500/15 text-xs font-semibold text-brand-600">
            {initials || '?'}
          </div>
          <span
            className={cn(
              'absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-surface-2',
              props.online ? 'bg-ok' : 'bg-ink-soft',
            )}
            aria-label={props.online ? 'Online' : 'Offline'}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink leading-tight">{props.peerName}</p>
          {props.subtitle && (
            <p className="truncate text-[11px] text-ink-subtle leading-tight">{props.subtitle}</p>
          )}
        </div>
      </header>

      <BodyAndComposer
        messages={props.messages}
        meId={props.meId}
        peerName={props.peerName}
        sending={props.sending}
        body={props.body}
        onBodyChange={props.onBodyChange}
        onKeyDown={props.onKeyDown}
        onSend={props.onSend}
        emojiOpen={props.emojiOpen}
        onToggleEmoji={props.onToggleEmoji}
        onInsertEmoji={props.onInsertEmoji}
        reactingTo={props.reactingTo}
        onStartReacting={props.onStartReacting}
        onToggleReaction={props.onToggleReaction}
        scrollRef={scrollRef}
        inputRef={inputRef}
      />
    </div>
  );
}

// ── Auto-expanding textarea ──────────────────────────────────────────

import { forwardRef, useImperativeHandle } from 'react';

const AutoExpandingTextarea = forwardRef<
  HTMLTextAreaElement,
  {
    value: string;
    onChange: (s: string) => void;
    onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
    disabled?: boolean;
  }
>(({ value, onChange, onKeyDown, placeholder, disabled }, ref) => {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(120, el.scrollHeight)}px`;
  }, [value]);

  return (
    <textarea
      ref={innerRef}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      className={cn(
        'flex-1 resize-none rounded-2xl border border-line bg-surface',
        'px-3 py-2 text-sm text-ink placeholder:text-ink-subtle',
        'focus:outline-none focus:border-line-strong transition-colors duration-120',
        'min-h-[40px] max-h-[120px]',
      )}
      style={{ overflow: 'hidden' }}
    />
  );
});
AutoExpandingTextarea.displayName = 'AutoExpandingTextarea';

// ── Helpers ──────────────────────────────────────────────────────────

function formatGroupTimestamp(iso: string, isEn: boolean): string {
  const d = parseServerDate(iso);
  if (!d) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const localeStr = isEn ? 'en-US' : 'pt-BR';
  const time = d.toLocaleTimeString(localeStr, { hour: '2-digit', minute: '2-digit', hour12: isEn });
  if (sameDay) return time;
  return `${d.toLocaleDateString(localeStr, { day: '2-digit', month: 'short' })} · ${time}`;
}

function relativeShort(iso: string | null | undefined, isEn: boolean): string {
  if (!iso) return '';
  const date = parseServerDate(iso);
  if (!date) return '';
  const t = date.getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return isEn ? 'now' : 'agora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d === 1) return isEn ? 'yesterday' : 'ontem';
  if (d < 7) return `${d}d`;
  return date.toLocaleDateString(isEn ? 'en-US' : 'pt-BR', { day: '2-digit', month: 'short' });
}

// Notification sound — best-effort. Som curto carregado on-demand.
// Se /sounds/chat-pop.mp3 não existir, swallow silently.
let popAudio: HTMLAudioElement | null = null;
function playPop() {
  try {
    if (typeof window === 'undefined') return;
    const enabled = window.localStorage?.getItem('chat.soundEnabled');
    if (enabled === '0') return;
    if (!popAudio) {
      popAudio = new Audio('/sounds/chat-pop.mp3');
      popAudio.volume = 0.4;
    }
    void popAudio.play().catch(() => {});
  } catch {
    // silencioso
  }
}
